import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useNumberRequests } from "@/features/number-requests/hooks/use-number-requests";
import { NUMBER_REQUEST_STATES_NEEDING_CLIENT } from "@/features/number-requests/types";
import {
  useOnboarding,
  useOnboardingClientId,
} from "@/features/onboarding/hooks/use-onboarding";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CheckCircleIcon, CircleIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HeyUpTeamContact } from "./heyup-team-contact";

/**
 * "Premiers pas", at the bottom of the sidebar once the wizard is done.
 *
 * Two steps and a phone number, no more: what the welcome dialog leaves out on
 * purpose (their software, their services) waits here, quietly, with the
 * HeyUp team one tap away for the rest. The label is a link and the circle is
 * the checkbox: opening a step is not doing it, so only the client ticks it
 * (and can untick it). Ticks and the closed card are remembered per tenant in
 * this browser — it is a nudge, and a nudge that comes back on another device
 * costs nothing.
 *
 * One step is not a nudge: a number request waiting on the client (not sent,
 * or documents missing). Its badge sits on "Canaux", far down a sidebar that
 * scrolls, so it is repeated here, first, for as long as the request waits —
 * and it cannot be ticked, since only completing the request settles it: the
 * circle leads to the channels page like the label does.
 */

const STEPS = [
  { key: "software", to: "settings/tools" },
  { key: "services", to: "settings/services" },
] as const;

const CHANNELS = "settings/connectors";

const storageKey = (clientId: string, what: "done" | "dismissed") =>
  `heyup.firstSteps.${what}.${clientId}`;

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode, full storage: the card simply forgets.
  }
};

export function FirstSteps() {
  const { t } = useTranslation("onboarding");
  const clientId = useOnboardingClientId();
  const isOwner = useHasRole("OWNER");
  const navigate = useNavigate();
  const { data } = useOnboarding({ enabled: isOwner });
  // Same query (and cache entry) as the sidebar's "Canaux" badge.
  const { data: numberRequests } = useNumberRequests(clientId, {
    enabled: isOwner,
  });
  const blockedRequests = (numberRequests || []).filter((request) =>
    NUMBER_REQUEST_STATES_NEEDING_CLIENT.includes(request.state),
  ).length;

  const [done, setDone] = useState<string[]>(() =>
    JSON.parse(read(storageKey(clientId, "done")) || "[]"),
  );
  const [dismissed, setDismissed] = useState(
    () => read(storageKey(clientId, "dismissed")) === "1",
  );

  if (!isOwner || dismissed || data?.onboarding.status !== "completed") {
    return null;
  }

  const toggle = (key: string) => {
    const next = done.includes(key)
      ? done.filter((k) => k !== key)
      : [...done, key];
    setDone(next);
    write(storageKey(clientId, "done"), JSON.stringify(next));
  };

  const dismiss = () => {
    setDismissed(true);
    write(storageKey(clientId, "dismissed"), "1");
  };

  return (
    <div className="bg-background space-y-2.5 rounded-xl border px-2 py-3">
      <div className="flex items-center justify-between gap-2 px-1">
        <p className="text-sm font-semibold">{t("firstSteps.title")}</p>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground -m-1 rounded p-1"
          onClick={dismiss}
          aria-label={t("firstSteps.dismiss")}
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      <ul className="space-y-0.5">
        {blockedRequests > 0 && (
          <li className="flex items-center gap-0.5">
            {/* The sidebar's red count, where the checkbox would be. */}
            <button
              type="button"
              className="hover:bg-muted shrink-0 rounded-md p-1"
              onClick={() => navigate(`/${clientId}/${CHANNELS}`)}
              aria-label={t("firstSteps.steps.number")}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white">
                {blockedRequests}
              </span>
            </button>
            <Link
              to={`/${clientId}/${CHANNELS}`}
              className="hover:bg-muted min-w-0 flex-1 rounded-md px-1 py-1 text-[13px] leading-snug font-medium"
            >
              {t("firstSteps.steps.number")}
            </Link>
          </li>
        )}
        {STEPS.map(({ key, to }) => {
          const isDone = done.includes(key);
          const label = t(`firstSteps.steps.${key}`);
          return (
            <li key={key} className="flex items-center gap-0.5">
              <button
                type="button"
                role="checkbox"
                aria-checked={isDone}
                aria-label={label}
                className="hover:bg-muted shrink-0 rounded-md p-1"
                onClick={() => toggle(key)}
              >
                {isDone ? (
                  <CheckCircleIcon
                    className="text-primary h-4 w-4"
                    weight="fill"
                  />
                ) : (
                  <CircleIcon className="text-muted-foreground h-4 w-4" />
                )}
              </button>
              <Link
                to={`/${clientId}/${to}`}
                className={cn(
                  "hover:bg-muted min-w-0 flex-1 rounded-md px-1 py-1 text-[13px] leading-snug",
                  isDone && "text-muted-foreground line-through",
                )}
              >
                {label}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="border-t px-1 pt-2.5">
        <HeyUpTeamContact compact />
      </div>
    </div>
  );
}
